import { SHButton, SHStack, SHTypography } from "@components/design-systems";
import { SHCollapse } from "@components/design-systems/sh-collapse";
import { useFeeCalculatorEditFamilyMember } from "@hooks/useFeeCalculatorEditFamilyMember";
import {
  FamilyMemberAccountDTO,
  FamilyMemberDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { useTheme } from "@mui/material";
import { hexToRGBA } from "@utils";
import { useMemo } from "react";
import { NumericFormat } from "react-number-format";
import { CardBalancesBox } from "../balances-box";
import { MemberCardName } from "../member-name";

type Props = {
  memberIndex: number;
  memberInfo?: FamilyMemberDTO;
  canRemove?: boolean;
  disabled?: boolean;
  maxAccountsReached?: boolean;
  onNameChange?: (newName: string) => void;
  onAddAccount?: ({ isIDPS }: { isIDPS: boolean }) => void;
  onRemove?: () => void;
  onBalanceRemove?: ({
    isIDPS,
    accountId,
  }: {
    isIDPS: boolean;
    accountId: string;
  }) => void;
};

export const FamilyMemberCard = ({
  memberIndex,
  memberInfo,
  canRemove,
  disabled,
  maxAccountsReached,
  onNameChange,
  onAddAccount,
  onRemove,
  onBalanceRemove,
}: Props) => {
  const { familyMemberAccounts } = memberInfo || {};
  const { palette } = useTheme();

  const { totalBalance } = useFeeCalculatorEditFamilyMember({
    memberIndex,
  });

  const { IDPSAccounts, superPensionAccounts } = useMemo(() => {
    const IDPSAccounts: FamilyMemberAccountDTO[] = [];
    const superPensionAccounts: FamilyMemberAccountDTO[] = [];

    familyMemberAccounts?.forEach((account, index) => {
      const updatedAccount = { ...account, accountIndex: index };
      if (account.isIDPS) {
        IDPSAccounts.push(updatedAccount);
      } else if (!account.isIDPS) {
        superPensionAccounts.push(updatedAccount);
      }
    });

    return { IDPSAccounts, superPensionAccounts };
  }, [familyMemberAccounts]);

  return (
    <SHCollapse
      displayArrow={false}
      sx={{ marginTop: memberIndex === 0 ? 0 : 3 }}
    >
      <SHStack
        width={"100%"}
        height={"fit-content"}
        border={`1px solid ${palette.divider}`}
      >
        {/* Header */}
        <SHStack
          padding={2}
          direction={"row"}
          justifyContent={"space-between"}
          bgcolor={hexToRGBA(palette.secondary[100], 0.25)}
        >
          <MemberCardName
            disabled={disabled}
            memberIndex={memberIndex}
            initName={memberInfo?.name}
            onChange={onNameChange}
          />

          <SHStack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"flex-end"}
            gap={1}
          >
            <SHStack direction={"row"} spacing={1}>
              <SHTypography variant="body2">Total assets</SHTypography>
              <SHTypography variant="subtitle2">
                $
                <NumericFormat
                  displayType="text"
                  value={totalBalance}
                  thousandSeparator=","
                  decimalScale={2}
                />
              </SHTypography>
            </SHStack>

            {canRemove && (
              <SHButton
                disabled={disabled}
                sx={{
                  color: palette.error.main,
                  fontSize: "14px",
                  fontWeight: 600,
                  p: 0,
                  paddingTop: "1px",
                  border: "none",
                  height: "auto",
                  "&:active": { border: "none" },
                  "&:hover": { color: palette.error.dark },
                }}
                variant="text"
                color="error"
                onClick={onRemove}
              >
                Remove
              </SHButton>
            )}
          </SHStack>
        </SHStack>

        {/* Content - Accounts List */}
        <SHStack
          width={"100%"}
          display={"grid"}
          gridTemplateColumns={"1fr 1fr"}
          borderTop={`1px solid ${palette.divider}`}
        >
          <CardBalancesBox
            disabled={disabled}
            memberIndex={memberIndex}
            title="IDPS"
            accounts={IDPSAccounts}
            maxAccountsReached={maxAccountsReached}
            onAdd={() => onAddAccount?.({ isIDPS: true })}
            onRemove={(accountId) =>
              onBalanceRemove?.({ isIDPS: true, accountId })
            }
            boxStyles={{ borderRight: `1px solid ${palette.divider}` }}
          />
          <CardBalancesBox
            disabled={disabled}
            memberIndex={memberIndex}
            title="Super/Pension"
            accounts={superPensionAccounts}
            maxAccountsReached={maxAccountsReached}
            onAdd={() => onAddAccount?.({ isIDPS: false })}
            onRemove={(accountId) =>
              onBalanceRemove?.({ isIDPS: false, accountId })
            }
          />
        </SHStack>
      </SHStack>
    </SHCollapse>
  );
};
